import {
  createDefaultRange,
  getTodayISO
} from '../helpers/specialPriceHelpers'
import {
  SPECIAL_PRICE_CREATE_ACTION_LABELS,
  SPECIAL_PRICE_CREATE_ALERT_MATCHERS,
  SPECIAL_PRICE_CREATE_ATTRIBUTE_FILTERS_INITIAL_STATE,
  SPECIAL_PRICE_CREATE_MESSAGES
} from './specialPriceCreateConstants'

export const createInitialAttributeFilters = () => ({
  ...SPECIAL_PRICE_CREATE_ATTRIBUTE_FILTERS_INITIAL_STATE
})

const normalizeAlertMessage = (message = '') =>
  message
    .replace(/^Validation failed:\s*/i, '')
    .replace(/\s+/g, ' ')
    .trim()

export const getFriendlyCreateSpecialPriceAlertConfig = (message) => {
  if (!message) return null

  const normalizedMessage = normalizeAlertMessage(message)

  if (normalizedMessage.includes(SPECIAL_PRICE_CREATE_ALERT_MATCHERS.productNotInPriceList)) {
    return {
      type: 'mui',
      message:
        'El producto seleccionado no está disponible para la lista de precios o filtro aplicado. Intente con otro producto o revise la configuración seleccionada.'
    }
  }

  if (normalizedMessage.includes(SPECIAL_PRICE_CREATE_ALERT_MATCHERS.overlap)) {
    return {
      type: 'mui',
      message:
        'Ya existe un precio especial para este mismo cliente o atributo, producto y unidad de venta dentro del rango seleccionado. Escoja otras fechas para continuar.'
    }
  }

  return {
    type: 'mui',
    message: normalizedMessage || SPECIAL_PRICE_CREATE_MESSAGES.saveError
  }
}

export const formatSpecialPriceProductOptionLabel = ({ code, name, unit }) => {
  const normalizedCode = String(code || '').trim()
  const normalizedName = String(name || '').trim()
  const normalizedUnit = String(unit || '').trim() || 'Sin unidad'

  return [normalizedCode, normalizedName, normalizedUnit].filter(Boolean).join(' - ')
}

export const buildCreateSpecialPriceCopyState = ({
  detail,
  availablePriceLists,
  availableAttributeDefinitions
}) => {
  const nextState = {
    scopeType: detail.scope_type,
    selectedClient: null,
    clientError: '',
    attributeFilters: createInitialAttributeFilters(),
    selectedProduct: {
      product_id: detail.product?.id,
      product_db_ref: detail.product?.db_ref,
      product_name: detail.product?.name,
      packages: [
        {
          uid: detail.package_uid,
          unit: detail.sales_unit === '-' ? '' : detail.sales_unit,
          quantity: '',
          price: detail.base_price
        }
      ]
    },
    selectedProductOption: {
      value: detail.product?.db_ref,
      label: formatSpecialPriceProductOptionLabel({
        code: detail.product?.db_ref,
        name: detail.product?.name,
        unit: detail.sales_unit === '-' ? '' : detail.sales_unit
      }),
      product: {
        product_id: detail.product?.id,
        product_db_ref: detail.product?.db_ref,
        product_name: detail.product?.name,
        packages: [
          {
            uid: detail.package_uid,
            unit: detail.sales_unit === '-' ? '' : detail.sales_unit,
            quantity: '',
            price: detail.base_price
          }
        ]
      }
    },
    selectedPackageUid: detail.package_uid,
    productStepRequiresReconfiguration: false,
    ranges:
      detail.ranges?.length > 0
        ? detail.ranges.map((range) => ({
            min_qty: range.min_qty,
            max_qty: range.max_qty ?? '',
            special_price: String(range.special_price)
          }))
        : [createDefaultRange()],
    startDate: '',
    endDate: ''
  }

  if (detail.scope_type === 'client') {
    nextState.selectedClient = {
      value: detail.client_db_ref,
      label: detail.scope_label || detail.client_db_ref,
      client: {
        db_ref: detail.client_db_ref,
        name: detail.scope_label || detail.client_db_ref,
        price_list: detail.scope_filters?.price_list_name || true
      }
    }
  }

  if (detail.scope_type === 'attribute') {
    const selectedPriceList = availablePriceLists.find((priceList) => {
      return (
        String(priceList.id) === String(detail.scope_filters?.price_list_id) ||
        priceList.db_ref === detail.scope_filters?.price_list_db_ref ||
        priceList.description === detail.scope_filters?.price_list_name
      )
    })

    nextState.attributeFilters = {
      ...createInitialAttributeFilters(),
      price_list_id: selectedPriceList ? String(selectedPriceList.id) : ''
    }

    availableAttributeDefinitions.forEach((definition) => {
      const values = detail.scope_filters?.[definition.key]
      nextState.attributeFilters[definition.key] = Array.isArray(values)
        ? values[0] || ''
        : values || ''
    })
  }

  return nextState
}

export const buildSelectedQualifierPayload = ({
  scopeType,
  selectedClient,
  attributeFilters
}) => {
  if (scopeType === 'client') {
    const qualifierValue =
      selectedClient?.client?.qualifier_1 ||
      selectedClient?.client?.db_ref ||
      selectedClient?.value

    return qualifierValue
      ? {
          qualifier_1: qualifierValue
        }
      : {}
  }

  return Object.keys(attributeFilters).reduce((accumulator, key) => {
    if (!key.startsWith('qualifier_') || !attributeFilters[key]) return accumulator

    return {
      ...accumulator,
      [key]: attributeFilters[key]
    }
  }, {})
}

export const syncSpecialPriceRanges = (nextRanges) => {
  let expectedMin = 1

  return nextRanges.map((range, index) => {
    const maxQty =
      range.max_qty === '' || range.max_qty === null || range.max_qty === undefined
        ? ''
        : Number(range.max_qty)

    const syncedRange = {
      ...range,
      min_qty: expectedMin
    }

    if (index !== nextRanges.length - 1 && maxQty !== '') {
      expectedMin = maxQty + 1
    } else if (index === nextRanges.length - 1 && maxQty !== '') {
      expectedMin = maxQty + 1
    }

    return syncedRange
  })
}

export const validateCreateSpecialPriceForm = ({
  scopeType,
  selectedClient,
  clientError,
  attributeScopeUnavailable,
  hasSelectedAttributeFilters,
  selectedProduct,
  productHasMissingSalesUnit,
  rangeValidationMessage,
  dateValidationErrors
}) => {
  const errors = {}

  if (!scopeType) {
    errors.scope_type = SPECIAL_PRICE_CREATE_MESSAGES.scopeTypeRequired
  }

  if (scopeType === 'client') {
    if (!selectedClient) {
      errors.client = SPECIAL_PRICE_CREATE_MESSAGES.clientRequired
    } else if (clientError) {
      errors.client = clientError
    }
  }

  if (scopeType === 'attribute') {
    if (attributeScopeUnavailable) {
      errors.attribute = SPECIAL_PRICE_CREATE_MESSAGES.attributeUnavailable
    }
    if (!hasSelectedAttributeFilters) {
      errors.attribute = SPECIAL_PRICE_CREATE_MESSAGES.attributeRequired
    }
  }

  if (!selectedProduct) {
    errors.product = SPECIAL_PRICE_CREATE_MESSAGES.productRequired
  } else if (productHasMissingSalesUnit) {
    errors.product = SPECIAL_PRICE_CREATE_MESSAGES.productMissingSalesUnit
  }

  if (rangeValidationMessage) {
    errors.ranges = rangeValidationMessage
  }

  if (dateValidationErrors.start_date) {
    errors.start_date = dateValidationErrors.start_date
  }

  if (dateValidationErrors.end_date) {
    errors.end_date = dateValidationErrors.end_date
  }

  return errors
}

export const buildCreateSpecialPricePayload = ({
  scopeType,
  selectedClient,
  scopeFiltersPayload,
  selectedProduct,
  selectedPackageUid,
  startDate,
  endDate,
  selectedQualifierPayload,
  ranges
}) => {
  const today = getTodayISO()
  const status = startDate > today ? 'scheduled' : 'active'

  return {
    scope_type: scopeType,
    client_db_ref:
      scopeType === 'client'
        ? selectedClient?.client?.db_ref || selectedClient?.value
        : undefined,
    scope_filters: scopeType === 'attribute' ? scopeFiltersPayload : undefined,
    product_db_ref: selectedProduct?.product_db_ref,
    package_uid: selectedPackageUid,
    start_date: startDate,
    end_date: endDate,
    status,
    active: status === 'active',
    ...selectedQualifierPayload,
    ranges: ranges.map((range, index) => ({
      min_qty: Number(range.min_qty),
      max_qty: range.max_qty === '' ? null : Number(range.max_qty),
      special_price: String(range.special_price).trim(),
      position: index + 1
    }))
  }
}

export const buildCreateSpecialPriceHeaderActions = ({
  isSaving,
  isFormValid,
  onCancel
}) => [
  {
    key: 'cancel',
    label: SPECIAL_PRICE_CREATE_ACTION_LABELS.cancel,
    onClick: onCancel,
    disabled: isSaving
  },
  {
    key: 'save',
    label: isSaving
      ? SPECIAL_PRICE_CREATE_ACTION_LABELS.saving
      : SPECIAL_PRICE_CREATE_ACTION_LABELS.save,
    type: 'submit',
    variant: 'contained',
    icon: 'check_circle',
    iconProps: {
      filled: false,
      size: 22,
      color: '#ffffff'
    },
    disabled: isSaving || !isFormValid
  }
]
